import { useLingui } from '@lingui/react/macro'
import { Text } from 'designSystem/components/Text'
import { FileText } from 'lucide-react'
import type { FC, ReactElement } from 'react'
import styled from 'styled-components'

import { TableHeadCell } from './entityPanelStyles'
import { SectionTitle } from './SectionTitle'
import { TruncatingLabel } from './TruncatingLabel'

const DocTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.75rem;
  line-height: 1.35;
`

const DocCell = styled.td`
  padding: 0.2rem 0.4rem 0.2rem 0;
  vertical-align: top;
  min-width: 0;
  overflow: hidden;
`

const DocCellInner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  font-size: inherit;
  color: var(--muted-foreground);
`

const DocTitle = styled.div`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
`

const SourceIcon = styled(FileText)`
  flex-shrink: 0;
  margin-top: 0.15rem;
  opacity: 0.7;
`

const DocLinkButton = styled.button`
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  color: var(--color-blue-400);

  &:hover {
    color: var(--color-blue-500);
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: progress;
  }

  &:focus-visible {
    outline: 0.125rem solid var(--ring);
    outline-offset: 0.125rem;
    border-radius: 0.125rem;
  }
`

const RevHeadCell = styled(TableHeadCell)`
  width: 2.75rem;
  padding-right: 0;
  text-align: right;
  white-space: nowrap;
`

const RevCell = styled.td`
  width: 2.75rem;
  padding: 0.2rem 0 0.2rem 0.3rem;
  vertical-align: top;
  text-align: right;
  white-space: nowrap;
  color: var(--muted-foreground);
`

export interface RelatedDocumentRow {
  key: string
  openId: string
  label: string
  revisionCode: string
  isCurrent: boolean
}

interface RelatedDocumentsTableProps<Row extends RelatedDocumentRow> {
  rows: Row[]
  openingDocId: string | null
  onPreload: (row: Row) => void
  onOpen: (row: Row) => void
}

export const RelatedDocumentsMessage: FC<{ message: string }> = ({
  message,
}) => {
  const { t } = useLingui()
  return (
    <>
      <SectionTitle>{t`Documents`}</SectionTitle>
      <Text as='span' variant='caption' color='muted'>
        {message}
      </Text>
    </>
  )
}

export const RelatedDocumentsTable = <Row extends RelatedDocumentRow>({
  rows,
  openingDocId,
  onPreload,
  onOpen,
}: RelatedDocumentsTableProps<Row>): ReactElement => {
  const { t } = useLingui()

  const youAreHereLabel = (row: Row) => {
    const label = row.label
    return t`${label} (You are here)`
  }

  const showRevColumn = rows.some((row) => row.revisionCode)

  return (
    <DocTable>
      <thead>
        <tr>
          <TableHeadCell>{t`Documents`}</TableHeadCell>
          {showRevColumn && <RevHeadCell>{t`Rev.`}</RevHeadCell>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <DocCell>
              <DocCellInner>
                <SourceIcon size={12} />
                <DocTitle>
                  {row.isCurrent ? (
                    <TruncatingLabel text={youAreHereLabel(row)} muted />
                  ) : (
                    <DocLinkButton
                      type='button'
                      disabled={openingDocId === row.openId}
                      onMouseEnter={() => onPreload(row)}
                      onFocus={() => onPreload(row)}
                      onClick={() => onOpen(row)}>
                      <TruncatingLabel text={row.label} />
                    </DocLinkButton>
                  )}
                </DocTitle>
              </DocCellInner>
            </DocCell>
            {showRevColumn && <RevCell>{row.revisionCode || null}</RevCell>}
          </tr>
        ))}
      </tbody>
    </DocTable>
  )
}
